'use client';

import type { ReactNode } from 'react';
import { Ban, CalendarClock, Check, Hourglass, LoaderCircle, Pause, SkipForward, TriangleAlert, type LucideIcon } from 'lucide-react';
import type { StatusGlyphProps, StatusKind, StatusMarkProps } from 'elowen-plugin-ui-kit';
import { Tooltip, TooltipAnchor, TooltipContent } from './shadcn/tooltip';
import { useTooltipAnchor } from './shadcn/useTooltipAnchor';
import { TONE_TEXT, type LabelTone, type Tone } from './tone';

const LABEL_DOT: Record<LabelTone, string> = {
  'label-1': 'bg-label-1',
  'label-2': 'bg-label-2',
  'label-3': 'bg-label-3',
  'label-4': 'bg-label-4',
  'label-5': 'bg-label-5',
  'label-6': 'bg-label-6',
};

const STATUS_ICONS: Record<StatusKind, LucideIcon> = {
  error: TriangleAlert, paused: Pause, overdue: CalendarClock, working: LoaderCircle,
  waiting: Hourglass, revoked: Ban, expired: CalendarClock, success: Check, skipped: SkipForward,
};

/** The non-interactive state glyph, shared with composite listbox options. */
export function StatusGlyph({ icon, kind, tone, size = 15 }: StatusGlyphProps) {
  const Icon = kind ? STATUS_ICONS[kind] : icon;
  if (!Icon) throw new Error('StatusGlyph requires a known kind or an icon');
  return <span aria-hidden className={`inline-flex size-5.5 shrink-0 items-center justify-center ${TONE_TEXT[tone]}`}><Icon size={size} className={`${TONE_TEXT[tone]}${kind === 'working' ? ' animate-spin motion-reduce:animate-none' : ''}`} /></span>;
}

/** A named state, whose icon conveys the state independently of its tone. */
export function StatusMark({ icon, kind, label, tone, actionLabel, onClick, disabled }: StatusMarkProps) {
  const anchor = useTooltipAnchor();
  return <Tooltip open={anchor.open} onOpenChange={anchor.onOpenChange}>
    <TooltipAnchor asChild>
      <button type="button" aria-label={actionLabel ? `${actionLabel}: ${label}` : label} aria-describedby={anchor.open ? anchor.tooltipId : undefined} disabled={disabled}
        {...anchor.hover} {...anchor.focus} onClick={(event) => { event.preventDefault(); event.stopPropagation(); onClick?.(); anchor.show(); }}
        className={`relative z-[var(--z-lift)] inline-flex size-5.5 shrink-0 items-center justify-center rounded-md hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${TONE_TEXT[tone]}`}>
        <StatusGlyph {...(kind ? { kind } : { icon: icon! })} tone={tone} />
      </button>
    </TooltipAnchor>
    <TooltipContent id={anchor.tooltipId} side="top" className="w-auto max-w-[min(--spacing(80),calc(100vw-var(--spacing)*6))] break-all px-2 py-1">{label}</TooltipContent>
  </Tooltip>;
}

/** A categorical label; dot lets an existing domain palette supply its own mark. */
export function LabelMark({ tone, children, dot }: { tone: Tone | LabelTone; children: ReactNode; dot?: ReactNode }) {
  return <span className="inline-flex min-w-0 items-center gap-1.5 text-meta text-muted-foreground">
    {dot ?? <span aria-hidden className={`size-1.5 shrink-0 rounded-full ${tone in LABEL_DOT ? LABEL_DOT[tone as LabelTone] : TONE_TEXT[tone as Tone] + ' bg-current'}`} />}
    {children}
  </span>;
}
